"use client"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import {
  RemittanceStanding,
  hasAnyRemittance,
  type RemittanceRow,
} from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"
import { FALLBACK_MUTED_CLASS, type TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"


const BAR_CLASS: Record<RemittanceStanding, string> = {
  [RemittanceStanding.Steady]: "bg-primary",
  [RemittanceStanding.Lower]: "bg-amber-500 dark:bg-amber-400",
  [RemittanceStanding.None]: "bg-transparent",
}

/**
 * Remittances received (F3): what each branch sent the parish this period, against the previous comparable
 * period (the tick on each bar). The data model records remittances but no amount DUE, so the tile compares with
 * the previous period instead of inventing a target.
 */
export function ChurchRemittancesTile({
  rows,
  currency,
  fallback = null,
}: {
  rows: readonly RemittanceRow[]
  currency: string
  /** Set when the tile shows the previous period because this one has no money yet: its label, bars muted. */
  fallback?: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const remitting = rows.filter((row) => row.standing !== RemittanceStanding.None).length

  return (
    <HomeTile
      title={t("widgets.churchRemittances.title")}
      subtitle={fallback?.label ?? t("widgets.churchRemittances.subtitle")}
      testId='church-tile-remittances'
    >
      {!hasAnyRemittance(rows) ? (
        <HomeTileEmpty message={t("church.remittances.empty")} />
      ) : (
        <>
          <ul
            data-fallback={fallback ? true : undefined}
            className={cn("-mr-1 flex min-h-0 flex-col gap-2 overflow-y-auto pr-1", fallback && FALLBACK_MUTED_CLASS)}
          >
            {rows.map((row) => (
              <li
                key={row.id}
                data-testid='church-remittance-row'
                className='grid grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)_minmax(0,15rem)] items-center gap-3 text-[12.5px]'
              >
                <span className='truncate font-semibold text-foreground'>{row.name}</span>
                <span className='relative block h-2 rounded-[4px] bg-muted'>
                  <span
                    className={cn("block h-full rounded-[4px]", BAR_CLASS[row.standing])}
                    style={{ width: `${row.ratio > 0 ? Math.max(2, row.ratio * 100) : 0}%` }}
                  />
                  {row.previousRatio > 0 ? (
                    <span
                      aria-hidden
                      className='absolute -top-[3px] -bottom-[3px] w-0.5 rounded-[1px] bg-foreground'
                      style={{ left: `calc(${row.previousRatio * 100}% - 1px)` }}
                    />
                  ) : null}
                </span>
                <span className='text-right whitespace-nowrap text-muted-foreground tabular-nums'>
                  <b className='text-foreground'>{formatCurrency(row.remitted, currency)}</b>
                  {" "}
                  {t("church.remittances.previous", { amount: formatCurrency(row.remittedPrevious, currency) })}
                </span>
              </li>
            ))}
          </ul>
          <span className='mt-auto text-[12.5px] text-muted-foreground'>
            {t("church.remittances.summary", { count: remitting, total: rows.length })}
          </span>
        </>
      )}
    </HomeTile>
  )
}
